import { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { ThumbsDown, ThumbsUp } from "lucide-react";
import type { PayloadPostMerchflowQaFeedback } from "@CommonApi/merchflows/_merchflowId_/qa";
import { Color, Flex, Text } from "src/elements";
import { useTranslation } from "src/i18n";
import { styledSafe } from "src/utils/styledComponent";
import { postMerchflowQaFeedback } from "../store/api";
import type { OQaEntry } from "../store/useOQa";
import { Composer, ComposerInput } from "../ConfigCopilotPanel";
import { LinkButton } from "./OParts";

export const FEEDBACK_COMMENT_MAX = 500;

const Thumb = styledSafe("button")`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  border: none;
  border-radius: 13px;
  background: none;
  color: ${Color.textSecondary};
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${Color.greenSmoke};
    color: ${Color.primaryActive};
  }

  &[aria-pressed="true"] {
    color: ${Color.primaryActive};
  }

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }

  &:focus-visible {
    outline: 2px solid ${Color.primaryActive};
    outline-offset: 1px;
  }
`;

interface Props {
  merchflowId: number;
  /** One of O's answers, with the question and page it answers. */
  entry: OQaEntry;
  onVoting: (entryId: number) => void;
  onVoted: (entryId: number) => void;
  onVoteFailed: (entryId: number, vote: Vote) => void;
}

type Vote = NonNullable<OQaEntry["failedVote"]>;

/** Thumbs up or down under one of O's answers. One vote; a failure only offers a retry. */
export const OAnswerFeedback = ({ merchflowId, entry, onVoting, onVoted, onVoteFailed }: Props) => {
  const { t } = useTranslation("common");
  const [isCommenting, setIsCommenting] = useState(false);
  const [comment, setComment] = useState("");
  const box = useRef<HTMLInputElement>(null);
  const voteMutation = useMutation({
    mutationFn: (payload: PayloadPostMerchflowQaFeedback) =>
      postMerchflowQaFeedback(merchflowId, payload),
  });

  // Not autoFocus: that scrolls the window to the box on a page wider than it.
  useEffect(() => {
    if (isCommenting) box.current?.focus({ preventScroll: true });
  }, [isCommenting]);

  if (entry.question === undefined || entry.page === undefined) return null;
  const { question, page } = entry;
  // The shared entry, not this mutation, says a vote is sending: a reopened drawer has a fresh one.
  const isBusy = voteMutation.isPending || entry.isVoting === true;
  if (entry.isVoted) {
    return (
      <Flex role="status" data-testid="o-ask-feedback-thanks">
        <Text variant="small1" color={Color.textSecondary}>
          {t("configCopilot.qa.feedbackThanks")}
        </Text>
      </Flex>
    );
  }

  const send = async (vote: Vote) => {
    if (isBusy) return;
    onVoting(entry.id);
    try {
      await voteMutation.mutateAsync({
        page,
        question,
        answer: entry.text,
        helpful: vote.helpful,
        ...(entry.answerId ? { answerId: entry.answerId } : {}),
        ...(vote.comment ? { comment: vote.comment } : {}),
      });
      onVoted(entry.id);
    } catch {
      // Silent: the route may not be there yet, and a vote is never worth an error line.
      onVoteFailed(entry.id, vote);
    }
  };

  // The shared entry, not this mutation, keeps a failed vote: the drawer may have closed since.
  const failed = entry.failedVote;
  if (failed) {
    return (
      <Flex data-testid="o-ask-feedback">
        <LinkButton
          type="button"
          disabled={isBusy}
          data-testid="o-ask-feedback-retry"
          onClick={() => void send(failed)}
        >
          {t("configCopilot.qa.feedbackRetry")}
        </LinkButton>
      </Flex>
    );
  }

  if (isCommenting) {
    return (
      <Composer
        data-testid="o-ask-feedback"
        onSubmit={(event) => {
          event.preventDefault();
          const said = comment.trim().slice(0, FEEDBACK_COMMENT_MAX);
          void send({ helpful: false, comment: said || undefined });
        }}
      >
        <ComposerInput
          ref={box}
          value={comment}
          maxLength={FEEDBACK_COMMENT_MAX}
          aria-label={t("configCopilot.qa.feedbackWhat")}
          placeholder={t("configCopilot.qa.feedbackWhat")}
          data-testid="o-ask-feedback-comment"
          onChange={(event) => setComment(event.currentTarget.value)}
        />
        <LinkButton
          type="submit"
          disabled={isBusy}
          style={{ padding: "0 10px" }}
          data-testid="o-ask-feedback-send"
        >
          {t("configCopilot.qa.feedbackSend")}
        </LinkButton>
      </Composer>
    );
  }

  return (
    <Flex gap="2px" align="center" data-testid="o-ask-feedback">
      <Thumb
        type="button"
        aria-label={t("configCopilot.qa.feedbackUp")}
        title={t("configCopilot.qa.feedbackUp")}
        disabled={isBusy}
        data-testid="o-ask-feedback-up"
        onClick={() => void send({ helpful: true })}
      >
        <ThumbsUp size={14} aria-hidden />
      </Thumb>
      <Thumb
        type="button"
        aria-label={t("configCopilot.qa.feedbackDown")}
        title={t("configCopilot.qa.feedbackDown")}
        disabled={isBusy}
        data-testid="o-ask-feedback-down"
        onClick={() => setIsCommenting(true)}
      >
        <ThumbsDown size={14} aria-hidden />
      </Thumb>
    </Flex>
  );
};
